import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export interface PublicCycleSession {
  token: string;
  password: string;
  playerId: number;
  playerName: string;
  groupId: string;
  groupName: string;
}

interface PublicCycleContextValue {
  session: PublicCycleSession | null;
  setSession: (session: PublicCycleSession | null) => void;
  clearSession: () => void;
  isPublicCycle: boolean;
}

const PublicCycleContext = createContext<PublicCycleContextValue | undefined>(undefined);

function storageKey(token: string) {
  return `fulbito_public_cycle_${token}`;
}

function readStoredSession(token: string): PublicCycleSession | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(storageKey(token));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PublicCycleSession;
    if (parsed.token !== token || !parsed.playerId || !parsed.password) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStoredSession(session: PublicCycleSession | null, token: string) {
  if (typeof window === "undefined") return;
  if (!session) {
    sessionStorage.removeItem(storageKey(token));
    return;
  }
  sessionStorage.setItem(storageKey(token), JSON.stringify(session));
}

export function PublicCycleProvider({
  token,
  children,
}: {
  token: string;
  children: ReactNode;
}) {
  const [session, setSessionState] = useState<PublicCycleSession | null>(() =>
    readStoredSession(token)
  );

  useEffect(() => {
    setSessionState(readStoredSession(token));
  }, [token]);

  const setSession = useCallback(
    (next: PublicCycleSession | null) => {
      setSessionState(next);
      writeStoredSession(next, token);
    },
    [token]
  );

  const clearSession = useCallback(() => {
    setSession(null);
  }, [setSession]);

  const value = useMemo(
    () => ({
      session,
      setSession,
      clearSession,
      isPublicCycle: true,
    }),
    [session, setSession, clearSession]
  );

  return (
    <PublicCycleContext.Provider value={value}>{children}</PublicCycleContext.Provider>
  );
}

export function usePublicCycle() {
  const ctx = useContext(PublicCycleContext);
  if (!ctx) {
    throw new Error("usePublicCycle must be used within PublicCycleProvider");
  }
  return ctx;
}

export function usePublicCycleOptional() {
  return useContext(PublicCycleContext);
}
